import {
  fieldAddProductVulcan,
  fieldNumberOfAccessPoints,
  fieldSelectTheDevice
} from '../../../utils/constant';
import { fieldInstallation } from '../../vulcanPricing/fibeInternet/fibreInternetFieldNames';

const MERAKI_DEVICE = 'Meraki MR28';
// The Business Wifi option carries the price of the FIRST access point: $30 when
// bundled with any other product, $20 when standalone. Every additional access
// point is $20 either way.
const BUNDLED_FIRST_ACCESS_POINT_PRICE = 30;
const STANDALONE_FIRST_ACCESS_POINT_PRICE = 20;
const ADDITIONAL_ACCESS_POINT_PRICE = 20;

/**
 * Partial pure rewrite of `FibreBusinessWifiHelpers`
 * (vulcanHelpers/fibreBusinessWifiHelpers.js) — the cross-field and installation
 * parts. The large province/speed **fibre-offer matrix** (`getValidOffersForSpeed`
 * + ATL/BTL filtering on `fibreOfferField`) is ported separately.
 *
 * Covered here:
 *  - `fibreInstallation` (Vulcan): forces the $100 Installation Fee value and
 *    DISABLES that option — a self value + option patch.
 *  - Business-Wifi cross-field derivation: selecting Business Wifi defaults the
 *    device to "Meraki MR28" and the access-point count to 1 (only while the
 *    count is still empty); deselecting clears both. In the original these were
 *    writes into `formFields[device].value` / `formFields[accessPoints].value`
 *    from the addProduct handler — here they become SELF-derivations on the
 *    device and access-point fields (the [[forms-core-fieldrules-pure-rewrite]]
 *    pattern), submitted via the engine's resolved-values payload.
 *
 * Note: the derived count/device live in field state + the resolved payload, not
 * the raw `values` state — so pricing sees them only once the engine feeds
 * resolved values into `context.values` (a Stage-3 engine item). Verified by
 * rules/fibreBusinessWifi.diff.test.js.
 *
 * @param {{orderData: Object, t: Object}} ctx
 * @returns {import('../vulcanFieldRules').VulcanRule}
 */
export function makeFibreBusinessWifiRule(ctx) {
  const { orderData, t } = ctx;
  const {
    vulBusinessWifi,
    vulHundredDollarInstallationFee,
    vulNumberOfAccessPoints,
    vulFirstAccessPoint,
    vulAdditionalAccessPoint
  } = t;

  return (field, state, values) => {
    const { name } = field;
    const options = state.options || field.options || [];

    // Vulcan: force + disable the $100 installation fee.
    if (name === fieldInstallation && orderData.isThisVulcanOrder) {
      return {
        value: [vulHundredDollarInstallationFee],
        options: options.map(o =>
          o.fields.value === vulHundredDollarInstallationFee
            ? { ...o, fields: { ...o.fields, disabledOption: true } }
            : o
        )
      };
    }

    // First access point price: set on the Business Wifi option itself.
    if (name === fieldAddProductVulcan) {
      const selected = values[fieldAddProductVulcan] || [];
      if (!selected.includes(vulBusinessWifi)) return undefined;
      const isBundled = selected.length > 1;
      const firstPrice = isBundled
        ? BUNDLED_FIRST_ACCESS_POINT_PRICE
        : STANDALONE_FIRST_ACCESS_POINT_PRICE;
      return {
        options: options.map(o =>
          o.fields.value === vulBusinessWifi
            ? { ...o, fields: { ...o.fields, monthlyPayment: firstPrice } }
            : o
        )
      };
    }

    if (name === fieldNumberOfAccessPoints || name === fieldSelectTheDevice) {
      // The original only derives when the addProduct value is truthy (its
      // handler was gated on `if (addProductVulcanValue)`); a falsy/unset
      // addProduct leaves device + count untouched.
      const addProduct = values[fieldAddProductVulcan];
      if (!addProduct) return undefined;
      const hasBusinessWifi = addProduct.includes(vulBusinessWifi);
      const accessPointsEmpty = !values[fieldNumberOfAccessPoints];

      // Access-point count: default 1 when Business Wifi is (newly) selected;
      // cleared when it is not.
      if (name === fieldNumberOfAccessPoints) {
        if (!hasBusinessWifi) return { value: undefined };
        const patch = { monthlyPayment: ADDITIONAL_ACCESS_POINT_PRICE };
        if (accessPointsEmpty) patch.value = 1;
        if (addProduct.length > 1) {
          patch.label = [
            vulNumberOfAccessPoints,
            `${vulFirstAccessPoint} ($${BUNDLED_FIRST_ACCESS_POINT_PRICE})`,
            `${vulAdditionalAccessPoint} ($${ADDITIONAL_ACCESS_POINT_PRICE})`
          ].join('\n');
        }
        return patch;
      }
      // Device: default Meraki alongside the count default; cleared with it.
      if (!hasBusinessWifi) return { value: '' };
      if (accessPointsEmpty) return { value: MERAKI_DEVICE };
      return undefined;
    }

    return undefined;
  };
}

export default makeFibreBusinessWifiRule;
